import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import PaymentModal from './PaymentModal';
import './Header.css';

const MENU = [
  {
    label: 'Recovery Housing',
    hub: '/recovery-housing/',
    items: [
      { label: 'Recovery Housing Overview', to: '/recovery-housing/' },
      { label: 'Recovery Housing Near Me', to: '/recovery-housing-near-me/' },
      { label: 'Recovery Housing Pennsylvania', to: '/recovery-housing-pennsylvania/' },
      { label: 'Recovery Residence', to: '/recovery-residence/' },
      { label: 'Transitional Recovery Housing', to: '/transitional-recovery-housing/' },
      { label: 'Structured Sober Living', to: '/structured-sober-living/' },
      { label: 'Sober Living', to: '/sober-living/' },
      { label: 'Sober Living Homes', to: '/sober-living-homes/' },
    ],
  },
  {
    label: "Men's Housing",
    hub: '/mens-recovery-housing/',
    items: [
      { label: "Men's Recovery Housing", to: '/mens-recovery-housing/' },
      { label: "Men's Sober Living", to: '/mens-sober-living/' },
      { label: 'Whitehall Men\u2019s Home', to: '/mens-recovery-housing-whitehall-pa/' },
      { label: "Men's Housing \u2013 Lehigh Valley", to: '/mens-recovery-housing-lehigh-valley-pa/' },
    ],
  },
  {
    label: "Women's Housing",
    hub: '/womens-recovery-housing/',
    items: [
      { label: "Women's Recovery Housing", to: '/womens-recovery-housing/' },
      { label: "Women's Sober Living", to: '/womens-sober-living/' },
      { label: 'Catasauqua Women\u2019s Home', to: '/womens-recovery-housing-catasauqua-pa/' },
      { label: "Women's Housing \u2013 Lehigh Valley", to: '/womens-recovery-housing-lehigh-valley-pa/' },
    ],
  },
  {
    label: 'Locations',
    hub: '/recovery-housing-lehigh-valley-pa/',
    items: [
      { label: 'Lehigh Valley, PA', to: '/recovery-housing-lehigh-valley-pa/' },
      { label: 'Whitehall, PA', to: '/recovery-housing-whitehall-pa/' },
      { label: 'Catasauqua, PA', to: '/recovery-housing-catasauqua-pa/' },
    ],
  },
  {
    label: 'Funding Options',
    hub: '/recovery-housing-funding-assistance/',
    items: [
      { label: 'Funding & Scholarship Assistance', to: '/recovery-housing-funding-assistance/' },
      { label: 'Recovery Housing Scholarships PA', to: '/recovery-housing-scholarships-pennsylvania/' },
      { label: 'County-Funded Recovery Housing', to: '/county-funded-recovery-housing-pennsylvania/' },
      { label: 'State-Funded Recovery Housing', to: '/state-funded-recovery-housing-pennsylvania/' },
      { label: 'DDAP Licensed Recovery Residences', to: '/ddap-licensed-recovery-housing-pennsylvania/' },
      { label: 'Payment Assistance', to: '/recovery-housing-payment-assistance/' },
    ],
  },
  {
    label: 'Resources',
    hub: '/resources/',
    items: [
      { label: 'Resources & Guides', to: '/resources/' },
      { label: 'What Is Recovery Housing?', to: '/what-is-recovery-housing/' },
      { label: 'Recovery Housing vs Sober Living', to: '/recovery-housing-vs-sober-living/' },
      { label: 'Benefits of Recovery Housing', to: '/benefits-of-recovery-housing/' },
      { label: 'Rules of a Recovery Home', to: '/rules-of-a-recovery-home/' },
      { label: 'Recovery Housing FAQ', to: '/recovery-housing-faq/' },
    ],
  },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState(-1);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const navRef = useRef(null);
  const location = useLocation();

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false);
    setOpenIndex(-1);
    setPaymentOpen(false);
  }, [location.pathname]);

  // Close on outside click / Escape
  useEffect(() => {
    const onClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenIndex(-1);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpenIndex(-1);
        setPaymentOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const toggle = (i) => setOpenIndex((prev) => (prev === i ? -1 : i));

  const openPaymentModal = () => {
    setMobileOpen(false);
    setOpenIndex(-1);
    setPaymentOpen(true);
  };

  return (
    <header className="header">
      <div className="header-container">
        <Link to="/" className="logo" aria-label="ProSolve Global Solutions Recovery Houses home">
          <span className="logo-text">ProSolve Global Solutions</span>
          <span className="logo-subtitle">Recovery Houses</span>
        </Link>

        <button
          className="mobile-menu-btn"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle navigation menu"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        <nav className={`nav ${mobileOpen ? 'nav-open' : ''}`} ref={navRef} aria-label="Primary">
          <ul className="nav-list">
            {MENU.map((group, i) => {
              const isOpen = openIndex === i;
              return (
                <li key={group.label} className={`nav-item has-dropdown ${isOpen ? 'open' : ''}`}>
                  <button
                    className="nav-link dropdown-toggle"
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    onClick={() => toggle(i)}
                  >
                    {group.label}
                    <ChevronDown size={15} className="nav-caret" aria-hidden="true" />
                  </button>
                  <ul className="dropdown">
                    {group.items.map((item) => (
                      <li key={item.to}>
                        <Link to={item.to} className="dropdown-link">{item.label}</Link>
                      </li>
                    ))}
                    {group.label === 'Resources' && (
                      <>
                        <li className="desktop-resource-item">
                          <a href="https://pgsrh.com/donations" className="dropdown-link">Donate</a>
                        </li>
                        <li className="desktop-resource-item">
                          <button
                            type="button"
                            className="dropdown-link dropdown-action"
                            onClick={openPaymentModal}
                          >
                            Make A Payment
                          </button>
                        </li>
                      </>
                    )}
                  </ul>
                </li>
              );
            })}
            <li className="nav-item nav-cta-item mobile-payment-item">
              <a href="https://pgsrh.com/donations" className="nav-link nav-contact-link">Donate</a>
            </li>
            <li className="nav-item nav-cta-item mobile-payment-item">
              <button
                type="button"
                className="nav-link nav-contact-link mobile-payment-action"
                onClick={openPaymentModal}
              >
                Make A Payment
              </button>
            </li>
            <li className="nav-item nav-cta-item">
              <Link to="/contact" className="nav-link nav-contact-link">Contact</Link>
            </li>
            <li className="nav-item">
              <Link to="/apply" className="apply-btn">Apply Now</Link>
            </li>
          </ul>
        </nav>
      </div>
      <PaymentModal open={paymentOpen} onClose={() => setPaymentOpen(false)} />
    </header>
  );
};

export default Header;
